// Phase SA-8 — Section C master surface (brief §06).
//
// Stats header + filter pills + CSV export button + per-slip rows
// (each expandable to <LegCardList />). Empty state when no
// autopsies. Soft-fails to empty per N01.

import { useMemo, useState } from "react";

import {
  DEFAULT_FILTERS,
  filterRows,
  aggregateStats,
  toCSV,
  type HistoryFilters,
} from "../../slips/autopsyHistoryHelpers.ts";
import { useAutopsyHistory } from "../../slips/useAutopsyHistory.ts";
import { AutopsyHistoryFilters } from "./AutopsyHistoryFilters.tsx";
import { AutopsyHistoryRow } from "./AutopsyHistoryRow.tsx";

export interface AutopsyHistoryProps {
  /** Pro tier flag — gates the CSV export button + propagates to
   * leg cards for full confidence score display. */
  pro?: boolean;
}

export function AutopsyHistory({ pro = false }: AutopsyHistoryProps): JSX.Element {
  const { rows, loading } = useAutopsyHistory();
  const [filters, setFilters] = useState<HistoryFilters>(DEFAULT_FILTERS);

  const visible = useMemo(() => filterRows(rows, filters), [rows, filters]);
  const stats = useMemo(() => aggregateStats(rows, 30), [rows]);

  if (loading) {
    return (
      <div
        data-testid="sa-history-loading"
        style={{
          padding: "12px 14px",
          fontFamily: "var(--sa-font-mono)",
          fontSize: 12,
          color: "var(--sa-fg-muted)",
        }}
      >
        Loading autopsy history…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        data-testid="sa-history-empty"
        style={{
          padding: "16px 18px",
          border: "1px dashed var(--sa-hairline)",
          borderRadius: 4,
          background: "var(--sa-bg-raised)",
          fontFamily: "var(--sa-font-body)",
          fontSize: 13.5,
          color: "var(--sa-fg-secondary)",
          lineHeight: 1.55,
        }}
      >
        No autopsies yet. Run your first autopsy above to start building your
        history.
      </div>
    );
  }

  const handleExport = (): void => {
    if (!pro) return;
    const csv = toCSV(visible);
    const filename = `strikza-autopsy-history-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    triggerCsvDownload(csv, filename);
  };

  return (
    <div
      data-testid="sa-history"
      style={{ display: "flex", flexDirection: "column", gap: 14 }}
    >
      {/* Stats header */}
      <div className="sa-history-stats" data-testid="sa-history-stats">
        <div className="sa-history-stats-headline">
          <div className="sa-history-stats-eyebrow">Last 30 days</div>
          <div
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 11,
              color: "var(--sa-fg-muted)",
              letterSpacing: "0.06em",
            }}
          >
            {`${rows.length} slips · ${rows.reduce(
              (s, r) => s + (r.leg_count ?? 0),
              0,
            )} legs total`}
          </div>
        </div>
        <div className="sa-history-stats-row">
          <Stat label="Slips" value={String(stats.slips)} />
          <Stat
            label="Win rate"
            value={
              stats.win_rate_pct == null
                ? "—"
                : `${Math.round(stats.win_rate_pct)}%`
            }
          />
          <Stat
            label="Process accuracy"
            value={
              stats.process_accuracy_pct == null
                ? "—"
                : `${Math.round(stats.process_accuracy_pct)}%`
            }
            tone="amber"
          />
        </div>
      </div>

      {/* Toolbar: filters + Export */}
      <div className="sa-history-toolbar" data-testid="sa-history-toolbar">
        <AutopsyHistoryFilters value={filters} onChange={setFilters} />
        <button
          type="button"
          className="sa-history-export-btn"
          onClick={handleExport}
          disabled={!pro || visible.length === 0}
          data-testid="sa-history-export-btn"
          data-pro={pro || undefined}
          title={
            pro
              ? "Download visible rows as CSV"
              : "CSV export — SIKA Pro feature"
          }
        >
          Export CSV
          {!pro ? (
            <span className="sa-history-export-pro-chip">🔒 PRO</span>
          ) : null}
        </button>
      </div>

      {/* Row list */}
      {visible.length === 0 ? (
        <div
          data-testid="sa-history-no-matches"
          style={{
            padding: "16px 18px",
            border: "1px dashed var(--sa-hairline)",
            borderRadius: 4,
            background: "var(--sa-bg-raised)",
            fontFamily: "var(--sa-font-mono)",
            fontSize: 12,
            color: "var(--sa-fg-muted)",
          }}
        >
          No slips match your current filters.
        </div>
      ) : (
        <div className="sa-history-rows">
          {visible.map((row) => (
            <AutopsyHistoryRow key={row.autopsy_id} row={row} pro={pro} />
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "amber";
}): JSX.Element {
  return (
    <div className="sa-history-stat">
      <span className="sa-history-stat-label">{label}</span>
      <span className="sa-history-stat-value" data-tone={tone}>
        {value}
      </span>
    </div>
  );
}

/** Tiny side-effect helper kept inline so the master component
 * remains a single file. SSR-safe (no-op on the server). */
function triggerCsvDownload(csv: string, filename: string): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  try {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[sa-history] CSV download failed:", e);
  }
}
